import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Category classifier — light heuristic based on product name so the panel stays self-contained.
// Order matters — Apparel is checked FIRST so "Rubbin' & Racin' Tee" doesn't match "Rub".
const CATEGORY_KEYWORDS = {
  Apparel: ["Tee", "Hat", "Cap"],
  Rubs: ["Rub", "Sweet Perfection", "Bliss"],
  Sauces: ["Sauce", "Gold", "Chipotle"],
  Bundles: ["Pack", "Gift"],
};
const categoryOf = (name) => {
  for (const [cat, keys] of Object.entries(CATEGORY_KEYWORDS)) {
    if (keys.some((k) => name.includes(k))) return cat;
  }
  return "Other";
};

export default function InventoryPanel() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [category, setCategory] = useState("All");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/inventory");
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      const d = {};
      list.forEach((it) => { d[it.product] = { ...(it.sizes || {}) }; });
      setDrafts(d);
    } catch { /* backend unreachable */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (product) => {
    setSaving(product);
    try {
      await api.put(`/owner/inventory/${encodeURIComponent(product)}`, { sizes: drafts[product] });
      toast.success(`${product} inventory saved`);
      await load();
    } catch {
      toast.error("Couldn't save inventory");
    } finally {
      setSaving(null);
    }
  };

  if (items.length === 0) return null;

  const filtered = category === "All" ? items : items.filter((it) => categoryOf(it.product) === category);
  const catCounts = ["All", "Rubs", "Sauces", "Bundles", "Apparel"].map((c) => ({
    key: c,
    n: c === "All" ? items.length : items.filter((it) => categoryOf(it.product) === c).length,
  }));

  return (
    <section data-testid="inventory-panel">
      <div>
        <h3 className="font-alfa text-xl uppercase">Inventory &amp; Stock ({items.length} items)</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
          Set the count for each size or one-size item. When a size hits 0 it&rsquo;s greyed out; when the total hits 0 the product flips to Sold Out.
        </p>

        {/* Category filter */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="inventory-cat-tabs">
          {catCounts.map(({ key, n }) => (
            <button
              key={key}
              type="button"
              onClick={() => setCategory(key)}
              data-testid={`inventory-cat-${key.toLowerCase()}`}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] ${category === key ? "border-ink bg-ink text-bone" : "border-ink/40 text-ink hover:bg-ink/5"}`}
            >
              {key} ({n})
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {filtered.map((it) => {
            const total = Object.values(drafts[it.product] || {}).reduce((a, b) => a + Number(b || 0), 0);
            const sizeKeys = Object.keys(it.sizes || {});
            const isOneSize = sizeKeys.length === 1;
            return (
              <div key={it.product} data-testid={`inv-${it.product}`} className={`border p-5 ${total === 0 ? "border-red-700 bg-red-50/50" : "border-ink bg-white"}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="font-alfa text-lg uppercase leading-tight">{it.product}</h4>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{categoryOf(it.product)}</p>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${total === 0 ? "text-red-700" : "text-ink/60"}`}>
                    {total === 0 ? "Sold Out" : `Total: ${total} in stock`}
                  </span>
                </div>
                <div className={`mt-4 grid gap-3 ${isOneSize ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-3 sm:grid-cols-6"}`}>
                  {sizeKeys.map((size) => (
                    <label key={size} className="flex flex-col gap-1">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                        {isOneSize ? "Stock" : `Size ${size}`}
                      </span>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        value={drafts[it.product]?.[size] ?? 0}
                        onChange={(e) => setDrafts((d) => ({ ...d, [it.product]: { ...(d[it.product] || {}), [size]: Number(e.target.value) } }))}
                        data-testid={`inv-${it.product}-${size}`}
                        className="border border-ink px-3 py-2 font-mono text-sm focus:outline-none"
                      />
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => save(it.product)}
                  disabled={saving === it.product}
                  data-testid={`inv-save-${it.product}`}
                  className="mt-4 inline-flex items-center gap-2 border-2 border-ink bg-ink px-5 py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone transition-colors hover:bg-white hover:text-ink disabled:opacity-60"
                >
                  {saving === it.product ? "Saving…" : "Save inventory"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
